import { ApiError } from './errors';

export interface ApiConfig {
  baseUrl: string;
  headers: Record<string, string>;
}

// Use proxy through Vite in development - this avoids CORS issues
// In production, VITE_API_URL should be set at build time
const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || '/api';
};

const API_URL = getApiUrl();

// Utility function to clean up malformed auth data
export const cleanAuthStorage = () => {
  if (typeof window !== 'undefined') {
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        const token = parsed.state?.token;
        
        // If token is string "null" or other invalid values, clear the storage
        if (token === 'null' || token === 'undefined' || token === '' || 
            (typeof token === 'string' && token.length < 10)) {
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('auth_token'); // Also remove individual token if exists
        }
      }
    } catch (error) {
      console.warn('Error cleaning auth storage:', error);
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('auth_token');
    }
  }
};

export const createApiConfig = (token?: string): ApiConfig => {
  // Clean up any malformed auth data first
  cleanAuthStorage();
  
  // Get current token from localStorage if not provided
  if (!token && typeof window !== 'undefined') {
    try {
      const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      token = authStorage.state?.token;
      
      // Additional check to ensure token is valid
      if (token === 'null' || token === 'undefined' || token === '' || !token) {
        token = null;
      }
    } catch (error) {
      console.warn('Failed to parse auth storage:', error);
      token = null;
    }
  }

  const config = {
    baseUrl: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && token !== 'null' && token !== 'undefined' && token !== '' ? { Authorization: `Bearer ${token}` } : {})
    }
  };

  return config;
};

export const handleResponse = async <T>(response: Response): Promise<T> => {
  try {
    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.message || 'Request failed', response.status);
    }

    // Handle _id and id format conversions
    if (Array.isArray(data)) {
      // Handle arrays (like getModules)
      return data.map(processItem) as T;
    } else if (data && typeof data === 'object') {
      // Handle single objects
      return processItem(data) as T;
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to parse server response', response.status);
  }
};

// Process an item to ensure id consistency
const processItem = (item: any): any => {
  if (!item || typeof item !== 'object') {
    return item;
  }
  
  // Create a new object with the processed properties
  const result = { ...item };
  
  // Ensure id exists (use _id as fallback)
  if (item._id && !item.id) {
    result.id = item._id;
  }
  
  // Process nested content array if it exists
  if (Array.isArray(item.content)) {
    result.content = item.content.map((contentItem: any) => {
      const processedContent = { ...contentItem };
      if (contentItem._id && !contentItem.id) {
        processedContent.id = contentItem._id;
      }
      return processedContent;
    });
  }
  
  // Process dates
  if (item.createdAt && typeof item.createdAt === 'string') {
    result.createdAt = new Date(item.createdAt);
  }
  
  if (item.updatedAt && typeof item.updatedAt === 'string') {
    result.updatedAt = new Date(item.updatedAt);
  }
  
  return result;
};